import Skeleton from 'react-loading-skeleton';

import GrowActionIcon from '~/shared/assets/icons/grow-action.svg';
import ShrinkActionIcon from '~/shared/assets/icons/shrink-action.svg';
import Card from '~/shared/components/Card';
import Typography from '~/shared/components/Typography';
import ActionTypeDropdown, {
  ActionTypeFilterValue,
} from '~/shared/components/filters/ActionTypeDropdown';

interface OptimizationActionsProps {
  selectedActionType: ActionTypeFilterValue;
  onActionTypeChange: (type: ActionTypeFilterValue) => void;
  typeCounts?: Partial<Record<ActionTypeFilterValue, number>>;
  typeCountsLoading?: boolean;
  allActionsCount?: number;
  allActionsCountLoading?: boolean;
}

export default function OptimizationActions({
  selectedActionType,
  onActionTypeChange,
  typeCounts = {},
  typeCountsLoading = false,
  allActionsCount,
  allActionsCountLoading,
}: OptimizationActionsProps) {
  const growCount = typeCounts['Growth'];
  const shrinkCount = typeCounts['Shrink'];

  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        {allActionsCount !== undefined && !allActionsCountLoading ? (
          <Typography fontWeight={500} variant="H5">
            Optimization Actions ({allActionsCount.toLocaleString()})
          </Typography>
        ) : (
          <Skeleton
            containerClassName="block leading-[unset!important]"
            className="h-[20px] leading-[unset!important] rounded-[8px]"
            baseColor="var(--color-gray-200)"
            highlightColor="#FFFF"
            width={150}
          />
        )}
      </div>
      <div className="flex gap-[14px] py-2.5 2xl:py-3.5">
        <OptimizationActionsCard
          title="Grow actions"
          icon={GrowActionIcon}
          count={growCount}
          isLoading={typeCountsLoading || growCount === undefined}
        />
        <OptimizationActionsCard
          title="Shrink actions"
          icon={ShrinkActionIcon}
          count={shrinkCount}
          isLoading={typeCountsLoading || shrinkCount === undefined}
        />
      </div>
      <div className="flex items-center py-2 2xl:py-3">
        <ActionTypeDropdown
          selectedType={selectedActionType}
          onSelect={onActionTypeChange}
          typeCounts={typeCounts}
          typeCountsLoading={typeCountsLoading}
          allActionsCount={allActionsCount}
          allActionsCountLoading={allActionsCountLoading}
        />
      </div>
    </div>
  );
}

function OptimizationActionsCard({
  title,
  icon: Icon,
  count,
  isLoading,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  count?: number;
  isLoading: boolean;
}) {
  return (
    <Card className="flex items-center gap-2 p-4 4xl:px-5 4xl:py-6">
      <Icon className="size-8" />
      <Typography fontWeight={500} variant="p3">
        {title}
      </Typography>
      {isLoading || count === undefined ? (
        <Skeleton
          containerClassName="ml-6 h-[24px] w-10 block my-auto leading-[unset!important]"
          className="h-[24px] leading-[unset!important]"
          baseColor="var(--color-gray-200)"
          highlightColor="#FFFF"
        />
      ) : (
        <Typography variant="H3-resp" color="text-[#2D2D32]" className="ml-6 font-family-outfit">
          {count.toLocaleString()}
        </Typography>
      )}
    </Card>
  );
}
